import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export const PageHero = ({ eyebrow, title, subtitle, accent, image }) => (
  <section className="relative pt-32 pb-20 lg:pt-44 lg:pb-28 overflow-hidden hero-backdrop">
    <div className="absolute inset-0 grain pointer-events-none" />
    <div className="max-w-[1440px] mx-auto px-6 lg:px-12 relative">
      <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className={`${image ? 'lg:col-span-7' : 'lg:col-span-9'} reveal`}>
          {eyebrow && <p className="section-label mb-8">{eyebrow}</p>}
          <h1 className="display-1 text-navy text-balance">
            {title}{accent && <span className="text-bronze"> {accent}</span>}
          </h1>
          {subtitle && (
            <p className="lede text-navy/70 text-lg lg:text-xl mt-8 max-w-3xl">
              {subtitle}
            </p>
          )}
        </div>
        {image && (
          <div className="lg:col-span-5 reveal">
            <div className="image-overlay aspect-[4/5] bg-navy-100">
              <img src={image} alt="" role="presentation" className="w-full h-full object-cover" loading="eager" />
            </div>
          </div>
        )}
      </div>
    </div>
  </section>
);

export const SectionLabel = ({ children, className = '' }) => (
  <p className={`section-label mb-6 ${className}`}>{children}</p>
);

export const PrimaryButton = ({ to, children, variant = 'primary', className = '' }) => {
  const VARIANT_STYLES = {
    primary: 'bg-navy text-cream hover:bg-navy-700',
    bronze: 'bg-bronze text-navy hover:bg-bronze-600',
    outline: 'bg-transparent text-navy border border-navy hover:bg-navy hover:text-cream',
  };
  const styles = VARIANT_STYLES[variant] || VARIANT_STYLES.outline;

  // Hash link → smooth scroll to element
  if (typeof to === 'string' && to.startsWith('#')) {
    const id = to.slice(1);
    return (
      <a
        href={to}
        onClick={(e) => {
          e.preventDefault();
          const el = document.getElementById(id);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            window.history.replaceState(null, '', to);
          }
        }}
        className={`btn-cta inline-flex items-center gap-3 px-7 py-4 text-sm tracking-wide transition-colors duration-300 ${styles} ${className}`}
      >
        {children}
        <ArrowLeft className="arrow w-4 h-4" />
      </a>
    );
  }

  return (
    <Link
      to={to}
      className={`btn-cta inline-flex items-center gap-3 px-7 py-4 text-sm tracking-wide transition-colors duration-300 ${styles} ${className}`}
    >
      {children}
      <ArrowLeft className="arrow w-4 h-4" />
    </Link>
  );
};

export const Stat = ({ value, suffix, label }) => (
  <div className="kpi-line pt-6">
    <div className="flex items-baseline gap-2">
      <span className="num-callout text-navy">{value}</span>
      {suffix && <span className="text-navy/60 text-sm font-medium">{suffix}</span>}
    </div>
    <p className="text-navy/60 text-sm mt-3 tracking-wide">{label}</p>
  </div>
);

export const QuoteRule = () => (
  <div className="flex items-center gap-3 mb-8">
    <span className="rule-line gold"></span>
  </div>
);
